import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
// Fare DEĞİL işaretçi olayları: aynı işleyici hem fareyi hem parmağı karşılıyor
// (`clientX/Y` ikisinde de var). Dokunmatikte `mousemove` yalnız dokunuş
// bittikten sonra uyumluluk amacıyla taklit ediliyordu, parmak sürüklenirken
// hiç gelmiyordu — grafik telefonda ölü bir yüzeydi.
import type { PointerEvent } from 'react'
import { clock, dolumEtiketi, price, qty } from '../format'
import type { Fill, Trade } from '../types'

/**
 * Seçili kontratın eşleşme grafiği: fiyat çizgisi + aynı düzlemde hacim sütunları.
 *
 * **X ekseni sıra bazlı, zaman orantılı değil.** Her eşleşme eşit genişlikte bir
 * bant alır; iki işlem arasında 3 saniye de olsa 20 dakika da olsa yatay mesafe
 * aynıdır. Sakin saatlerdeki tek tük işlemler bir noktaya sıkışmasın diye böyle.
 * Gerçek zaman x ekseni etiketlerinden ve tooltip'ten okunur.
 *
 * Hacim fiyatla aynı çizim alanında duruyor. Bu, iki ölçeğin üst üste binmesi
 * demek; yanıltıcı olmamasını sağlayan şey hacmin **kendi ekseninin olmaması**,
 * tabana sabitlenip alanın ~1/5'iyle sınırlanması ve fiyatın arkasında soluk
 * kalması — kimse hacmi fiyat ekseninden okumaz. Sayısal değeri tooltip'te ve
 * yandaki eşleşme listesinde (grafiğin tablo ikizi) durur.
 *
 * **Gölge dolumlarımız yön renginde nokta olarak işaretlenir** (alış yeşil,
 * satış kırmızı). Fiyat çizgisine,
 * alanına ve hacim sütunlarına hiç dokunmazlar — ayrı bir katman, birbirine
 * bağlanmıyorlar. Nokta, dolumu tetikleyen gerçek işlemin x konumunda ama
 * **bizim limit fiyatımızın** y konumunda durur; ikisi arasındaki dikey fark
 * lehimize olan sapmadır (agresörün ödeyeceği fark, bize gelmez).
 *
 * Dolum noktaları fareyle **mesafeye göre** yakalanıyor (`FILL_HIT_RADIUS`),
 * işlem bandına göre değil: gün dolduğunda bir işlem ~0,6 piksel yer kaplıyor
 * ve fare koordinatı tamsayı olduğu için bazı bantlara denk gelmek imkânsız
 * oluyordu — noktanın üstüne gelmek "tutmuyordu".
 */

interface Props {
  trades: Trade[]
  contract: string | null
  /** Gölge dolumlarımız — yön renginde nokta; çizgiye karışmaz. */
  fills?: Fill[]
  /** PTF — yatay kesikli referans çizgisi olarak çizilir. */
  mcp?: number | null
}

interface FillPoint {
  x: number
  y: number
  /** Aynı işlemden birden çok emrimiz dolabilir; toplam gösteriliyor. */
  quantity: number
  price: number
  side: 'BUY' | 'SELL'
  tradePrice: number
  ts: number
  /** Bu işlemden dolan emir sayısı (>1 ise fiyat ağırlıklı ortalama). */
  count: number
  /** Dolumun kaynağı — tooltip'te kısa adıyla (Simüle/Süpürdük/Süpürüldük). */
  liquidity?: 'trade' | 'book' | 'sweep'
}

// right: yalnız son noktanın işareti (yarıçap + yüzey halkası) kırpılmasın
// diye. Son fiyat etiketi kaldırıldığı için çizim alanı sağa kadar uzanıyor.
const PAD = { top: 14, right: 10, bottom: 22, left: 56 }
const VOLUME_SHARE = 0.22 // hacim sütunları çizim alanının en fazla bu kadarını kaplar
const MAX_BAR = 24 // mark spec: sütun kalınlığı en fazla 24px
const BAR_GAP = 2 // yüzey boşluğu — komşu sütunlar çerçeveyle değil boşlukla ayrılır
const LABEL_SPACING = 110 // x ekseni etiketleri arası en az mesafe (çakışmasın)
/** Gölge dolum noktasının isabet yarıçapı (px). Noktanın kendisi 4,5 px;
 *  bu, üstüne "tam" gelmeye çalışmadan yakalanabilmesi için geniş tutuldu. */
const FILL_HIT_RADIUS = 16

interface Point {
  trade: Trade
  x: number
  y: number
}

interface Layout {
  points: Point[]
  /** Gölge dolum işaretleri — ayrı katman, çizgiden bağımsız. */
  fillPoints: FillPoint[]
  /** İşlem kimliği → o işlemde dolan toplam miktarımız (tooltip için). */
  fillByTrade: Map<string, { quantity: number; price: number }>
  line: string
  area: string
  /** Tüm hacim sütunları TEK path'te — günün tamamı çizildiğinde binlerce
   *  ayrı SVG elemanı her saniye yeniden oluşturulmasın. */
  volume: string
  plotWidth: number
  floor: number // çizim alanının tabanı — hacim sütunları buradan büyür
  band: number
  ticks: number[]
  xLabels: { x: number; text: string }[]
  y: (p: number) => number
}

export function TradeChart({ trades, contract, fills = [], mcp = null }: Props) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [hover, setHover] = useState<Point | null>(null)
  // Gölge dolum noktası ayrı tutuluyor: işlem bandına göre değil FARE
  // MESAFESİNE göre seçiliyor (aşağıdaki `handleMove`).
  const [hoverFill, setHoverFill] = useState<FillPoint | null>(null)

  // Panel esnek yükseklikte; SVG gerçek piksel ölçüsüne göre çiziliyor ki
  // viewBox esnetmesi yazıları bozmasın ve eksen bandı dışarıda kalmasın.
  //
  // Ölçü HER RENDER'da yenileniyor. Yalnız mount anındaki tek ölçüye +
  // ResizeObserver'a güvenmek kırılgandı: mount anında kutu henüz
  // boyutlanmamışsa (sekme arka planda, panel geç yerleşiyor) ölçü 0 kalıyor ve
  // sayfa kare üretmediği için gözlemci de hiç tetiklenmiyordu — grafik
  // "Çiziliyor…" ekranında kalıcı olarak takılıyordu. Eşitlik kontrolü döngüyü
  // engelliyor, ölçüm değişmediğinde yeni render tetiklenmiyor.
  const measure = () => {
    const el = boxRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
  }
  useLayoutEffect(measure)

  // Yeniden render doğurmayan boyut değişimleri için (pencere boyutlandırma).
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Eskiden yeniye — liste yeniden eskiye geliyor.
  const ordered = useMemo(() => [...trades].reverse(), [trades])

  const layout = useMemo<Layout | null>(() => {
    const { width, height } = size
    if (width < 140 || height < 120 || ordered.length === 0) return null

    const plotWidth = width - PAD.left - PAD.right
    const plotHeight = height - PAD.top - PAD.bottom
    const floor = PAD.top + plotHeight

    // Aynı işlemde birden çok emrimiz dolabilir (aynı fiyattan iki emir):
    // işlem başına toplanıyor, yoksa noktalar üst üste biner.
    type Toplam = {
      quantity: number; price: number; side: 'BUY' | 'SELL'
      tradePrice: number; ts: number; count: number
      liquidity?: 'trade' | 'book' | 'sweep'
    }
    const fillByTrade = new Map<string, Toplam>()
    for (const fill of fills) {
      const onceki = fillByTrade.get(fill.tradeId)
      const miktar = (onceki?.quantity ?? 0) + fill.quantity
      fillByTrade.set(fill.tradeId, {
        // Birden çok emrimiz aynı işlemden dolduysa fiyat MİKTARLA AĞIRLIKLI
        // ortalanıyor; düz ortalama küçük dolumu büyükle eşit sayardı.
        quantity: miktar,
        price: onceki
          ? (onceki.price * onceki.quantity + fill.price * fill.quantity) / miktar
          : fill.price,
        side: fill.side,
        tradePrice: fill.tradePrice,
        ts: fill.ts,
        count: (onceki?.count ?? 0) + 1,
        liquidity: fill.liquidity ?? 'trade',
      })
    }

    // PTF de ölçeğe dahil: işlem aralığının dışında kalırsa referans çizgisi
    // çizim alanının dışına düşer ve hiç görünmez.
    //
    // Dolum fiyatlarımız da aynı sebeple dahil: limitimiz işlem aralığının
    // dışında kalabilir (fiyat iyileştirip beklerken piyasa hiç oraya
    // gelmemişse). Kırpmak yerine ölçeği genişletiyoruz — kırpılmış nokta
    // yanlış fiyat gösterir, ölçeğin birkaç piksel kayması ise zararsız.
    const fiyatlar = ordered.map((t) => t.price)
    if (mcp != null) fiyatlar.push(mcp)
    for (const f of fillByTrade.values()) fiyatlar.push(f.price)
    let pMin = Math.min(...fiyatlar)
    let pMax = Math.max(...fiyatlar)
    if (pMin === pMax) {
      // Tek fiyat seviyesi: çizgi dikeyde ortalansın, tabana yapışmasın.
      pMin -= 1
      pMax += 1
    }
    const headroom = (pMax - pMin) * 0.08
    pMin -= headroom
    pMax += headroom

    // Sıra bazlı x: her işlem eşit genişlikte bir bandın ortasında durur.
    const band = plotWidth / ordered.length
    const x = (index: number) => PAD.left + band * (index + 0.5)
    const y = (p: number) => PAD.top + (1 - (p - pMin) / (pMax - pMin)) * plotHeight

    const points: Point[] = ordered.map((trade, index) => ({
      trade,
      x: x(index),
      y: y(trade.price),
    }))

    // Dolum noktaları: x tetikleyen gerçek işlemin bandından, y dolum
    // fiyatından. Çizgi/alan/hacim hesaplarına hiç girmiyorlar.
    //
    // Tahta kaynaklı dolumların (Süpürdük/Süpürüldük) tetikleyen işlemi YOK —
    // `tradeId` tüketilen gerçek emrin kimliği. Onlar ZAMANA göre yerleşir:
    // dolum anından önceki son işlemin bandı (sıra bazlı eksende zamanın
    // karşılığı bu). Kullanıcı isteği 2026-07-29: süpürmeler grafikte görünsün.
    const fillPoints: FillPoint[] = []
    if (fillByTrade.size) {
      const indexOf = new Map(ordered.map((t, i) => [t.id, i]))
      const zamanaGore = (ts: number) => {
        let index = 0
        for (let i = 0; i < ordered.length; i += 1) {
          if (ordered[i]!.ts <= ts) index = i
          else break
        }
        return index
      }
      for (const [tradeId, f] of fillByTrade) {
        const index = indexOf.get(tradeId) ?? (f.liquidity !== 'trade' ? zamanaGore(f.ts) : null)
        if (index == null) continue     // tetikleyen işlem bu pencerede yok
        fillPoints.push({ x: x(index), y: y(f.price), ...f })
      }
    }

    const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')
    const first = points[0]!
    const last = points[points.length - 1]!
    const area = `${line} L${last.x} ${floor} L${first.x} ${floor} Z`

    // Zaman etiketleri: sıra ekseninde zaman doğrusal değil, o yüzden aralara da
    // damga koyuyoruz — yalnız uçlar olsa ortadaki yoğunluk okunmaz.
    const labelCount = Math.max(2, Math.min(6, Math.floor(plotWidth / LABEL_SPACING)))
    const xLabels = Array.from({ length: labelCount }, (_, i) => {
      const index = Math.round((i / (labelCount - 1)) * (ordered.length - 1))
      return { x: points[index]!.x, text: clock(ordered[index]!.ts) }
    })

    // Hacim sütunları tek bir path'te birleştiriliyor.
    const barWidth = Math.max(1, Math.min(MAX_BAR, band - BAR_GAP))
    const volumeMax = plotHeight * VOLUME_SHARE
    const qMax = Math.max(...ordered.map((t) => t.quantity), 1)
    const volume = points
      .map((p) => {
        const h = Math.max(1, (p.trade.quantity / qMax) * volumeMax)
        return barPath(p.x - barWidth / 2, floor - h, barWidth, h)
      })
      .join(' ')

    return {
      points,
      fillPoints,
      fillByTrade,
      line,
      area,
      volume,
      plotWidth,
      floor,
      band,
      ticks: priceTicks(pMin, pMax, 4),
      xLabels,
      y,
    }
  }, [size, ordered, fills, mcp])

  const handleMove = useCallback(
    (event: PointerEvent<SVGSVGElement>) => {
      if (!layout) return
      const rect = event.currentTarget.getBoundingClientRect()
      const mx = event.clientX - rect.left
      const my = event.clientY - rect.top

      // Gölge dolumlar ÖNCE ve mesafeye göre aranıyor. Bant aritmetiğiyle
      // seçilemiyorlardı: gün dolduğunda bir işlem ~0,6 piksel yer kaplıyor,
      // yani fare koordinatı tamsayı olduğu için bazı bantlara denk gelmek
      // imkânsız. Nokta az sayıda olduğu için hepsini gezip en yakınını almak
      // ucuz ve isabet yarıçapı bandın 20 katı.
      let enYakin: FillPoint | null = null
      let enKisa = FILL_HIT_RADIUS
      for (const f of layout.fillPoints) {
        const d = Math.hypot(f.x - mx, f.y - my)
        if (d <= enKisa) {
          enKisa = d
          enYakin = f
        }
      }
      setHoverFill(enYakin)

      // Sıra ekseninde en yakın işlem: bant genişliği sabit, isabet alanı da.
      const index = Math.floor((mx - PAD.left) / layout.band)
      const clamped = Math.min(Math.max(index, 0), layout.points.length - 1)
      setHover(layout.points[clamped] ?? null)
    },
    [layout],
  )

  const last = layout?.points[layout.points.length - 1]

  return (
    <div className="panel chart-panel">
      <div className="panel-title">Eşleşme Grafiği</div>

      <div className="chart-box" ref={boxRef}>
        {!layout || !last ? (
          <div className="chart-empty">
            {ordered.length === 0 ? 'Bu kontratta henüz işlem yok.' : 'Çiziliyor…'}
          </div>
        ) : (
          <svg
            className="chart-svg"
            // Koordinat uzayı ölçülen piksel değeri, ama eleman kutusunu CSS
            // dolduruyor: ölçü bir kare geciktiğinde SVG kutusundan taşıp iç
            // kaydırma çubuğu üretmesin (x ekseni bandı kırpılmasın). Steady
            // state'te ikisi birebir aynı, esneme sıfır.
            viewBox={`0 0 ${size.width} ${size.height}`}
            preserveAspectRatio="none"
            onPointerMove={handleMove}
            // Dokunuşta da anında okuma çıksın: parmak basar basmaz konum belli,
            // ayrıca bir "hareket" beklemeye gerek yok.
            onPointerDown={handleMove}
            onPointerLeave={(event) => {
              // Parmak kalkınca `pointerleave` de tetikleniyor. Dokunmatikte
              // balonu orada kapatmak okumaya fırsat bırakmazdı; yalnız fare
              // yüzeyden ayrıldığında temizleniyor.
              if (event.pointerType !== 'mouse') return
              setHover(null)
              setHoverFill(null)
            }}
            role="img"
            aria-label={`${contract ?? ''} eşleşme fiyat ve hacim grafiği`}
          >
            {/* Izgara: yüzeyden bir ton açık, kesintisiz hairline */}
            {layout.ticks.map((tick) => (
              <g key={tick}>
                <line
                  className="chart-grid"
                  x1={PAD.left}
                  x2={PAD.left + layout.plotWidth}
                  y1={layout.y(tick)}
                  y2={layout.y(tick)}
                />
                <text className="chart-tick" x={PAD.left - 8} y={layout.y(tick) + 3.5}>
                  {price(tick)}
                </text>
              </g>
            ))}

            {/* Hacim — fiyatın ARKASINDA, tabandan büyür, ucu yuvarlak tabanı düz.
                Kendi ekseni yok: büyüklük karşılaştırması için, değer okumak için değil. */}
            <path className="chart-volume" d={layout.volume} />

            <path className="chart-area" d={layout.area} />
            <path className="chart-line" d={layout.line} />

            {/* PTF — yatay kesikli referans çizgisi. Kesik çizgi burada doğru
                kullanım: bir eşik/referans, ızgara değil. */}
            {mcp != null && (
              <g>
                <line
                  className="chart-ptf"
                  x1={PAD.left}
                  x2={PAD.left + layout.plotWidth}
                  y1={layout.y(mcp)}
                  y2={layout.y(mcp)}
                />
                <text
                  className="chart-ptf-label"
                  x={PAD.left + 4}
                  // Çizgi tepeye yakınsa etiket altına geçer, tepeden taşmasın.
                  y={layout.y(mcp) < PAD.top + 14 ? layout.y(mcp) + 12 : layout.y(mcp) - 5}
                >
                  PTF {price(mcp)}
                </text>
              </g>
            )}

            {/* Gölge dolumlarımız — mor nokta. Birbirine BAĞLANMIYOR ve fiyat
                çizgisine karışmıyor: bunlar bir seri değil, tek tek olaylar.
                Yüzey halkası çizginin üstüne denk geldiğinde nokta kaybolmasın
                diye (fiyatımız işlem fiyatına eşitse tam üstünde durur). */}
            {layout.fillPoints.map((f, i) => (
              <circle
                key={`${f.x}-${i}`}
                className={`chart-fill-dot ${f.side === 'BUY' ? 'buy' : 'sell'}`}
                cx={f.x}
                cy={f.y}
                r={hoverFill === f ? 6 : 4.5}
              />
            ))}

            {/* Son işlemin işareti. Değer yazılmıyor — fiyat y ekseninden,
                tam değer tooltip'ten ve yandaki eşleşme listesinden okunuyor. */}
            <circle className="chart-dot" cx={last.x} cy={last.y} r={4} />

            {/* Dolum üzerindeyken dikey kılavuz o noktaya çekiliyor: hangi
                dolumun okunduğu belirsiz kalmasın. */}
            {(hoverFill || hover) && (
              <g>
                <line
                  className="chart-crosshair"
                  x1={hoverFill?.x ?? hover!.x}
                  x2={hoverFill?.x ?? hover!.x}
                  y1={PAD.top}
                  y2={layout.floor}
                />
                {!hoverFill && hover && (
                  <circle className="chart-dot" cx={hover.x} cy={hover.y} r={4} />
                )}
              </g>
            )}

            {layout.xLabels.map((label, i) => (
              <text
                key={`${label.x}-${i}`}
                className="chart-tick"
                x={label.x}
                y={size.height - 6}
                textAnchor={i === 0 ? 'start' : i === layout.xLabels.length - 1 ? 'end' : 'middle'}
              >
                {label.text}
              </text>
            ))}
          </svg>
        )}

        {/* Gölge dolum üzerindeyken ONUN kutusu gösteriliyor: kullanıcı noktayı
            hedefliyorsa istediği bilgi odur, altındaki işlem değil. */}
        {hoverFill && (
          <div
            className={`chart-tooltip fill ${hoverFill.side === 'BUY' ? 'buy' : 'sell'}`}
            style={{
              left: Math.min(Math.max(hoverFill.x + 12, 8), Math.max(size.width - 160, 8)),
              top: Math.max(hoverFill.y - 62, 8),
            }}
          >
            <div className="tooltip-time">
              {clock(hoverFill.ts)} · Gölge {hoverFill.side === 'BUY' ? 'alış' : 'satış'}
              {` · ${dolumEtiketi(hoverFill.liquidity)}`}
              {hoverFill.count > 1 && ` (${hoverFill.count} emir)`}
            </div>
            <div className="tooltip-row">
              <span>Miktar</span>
              <b>{qty(hoverFill.quantity)} lot</b>
            </div>
            <div className="tooltip-row">
              <span>Bizim fiyat</span>
              <b>{price(hoverFill.price)}</b>
            </div>
            {/* Piyasanın fiyatı ayrı satırda: ikisi karışırsa gerçekte
                kapatılamayacak bir kâr okunur. */}
            <div className="tooltip-row">
              <span>İşlem fiyatı</span>
              <b>{price(hoverFill.tradePrice)}</b>
            </div>
          </div>
        )}

        {hover && !hoverFill && (
          <div
            className="chart-tooltip"
            style={{
              left: Math.min(Math.max(hover.x + 12, 8), Math.max(size.width - 140, 8)),
              top: Math.max(hover.y - 46, 8),
            }}
          >
            <div className="tooltip-time">{clock(hover.trade.ts)}</div>
            <div className="tooltip-row">
              <span>Fiyat</span>
              <b>{price(hover.trade.price)}</b>
            </div>
            <div className="tooltip-row">
              <span>Miktar</span>
              <b>{qty(hover.trade.quantity)}</b>
            </div>
            {/* Bu işlem bizi doldurduysa: kendi miktarımız ve KENDİ fiyatımız.
                Ayrı satır — işlemin kendi sayılarına karışmıyor. */}
            {(() => {
              const bizim = layout?.fillByTrade.get(hover.trade.id)
              if (!bizim) return null
              return (
                <div className="tooltip-row mine">
                  <span>Gölge dolum</span>
                  <b>
                    {qty(bizim.quantity)} @ {price(bizim.price)}
                  </b>
                </div>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}

/** Ucu yuvarlak, tabanı düz sütun — mark spec: 4px rounded data-end. */
function barPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h)
  return (
    `M${x} ${y + h} L${x} ${y + r} Q${x} ${y} ${x + r} ${y} ` +
    `L${x + w - r} ${y} Q${x + w} ${y} ${x + w} ${y + r} L${x + w} ${y + h} Z`
  )
}

/** Yuvarlak sayılara oturan y ekseni değerleri (1 / 2 / 2.5 / 5 katları). */
function priceTicks(min: number, max: number, count: number): number[] {
  const raw = (max - min) / count
  if (!Number.isFinite(raw) || raw <= 0) return []
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const ticks: number[] = []
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) ticks.push(v)
  return ticks
}
